import React, { useState, useEffect, useCallback } from 'react';
import { Formik, FieldArray, Form } from 'formik';
import AccessDenied from '@rsos/components/capstone/AccessDenied/AccessDenied';
import { Button, Text, Box } from '@rsos/components/capstone/base';
import { notifyError } from '@rsos/utils/toastUtils';
import { isAdminApproved } from '../../utils/isAdminApproved';
import { AddUserModal, EditUserModal, DeleteUserModal } from './Modals';
import UserSearchBar from './UserSearchBar';
import UsersContainer from './UsersContainer/UsersContainer';
import { handleSortingPSAPUsers } from './handleSortingPSAPUsers';
import { AddButtonContainer, UsersPageContainer } from './styles/Users.styles';

const Users = ({
  orgID,
  allRoles,
  currentPSAPUsers,
  jurisdictions = [],
  application,
  handleAccessDeniedLink = null,
}) => {
  const [addUserModalIsActive, setAddUserModalIsActive] = useState(false);
  const [editUserModalIsActive, setEditUserModalIsActive] = useState(false);
  const [deleteUserModalIsActive, setDeleteUserModalIsActive] = useState(false);
  const [isSelectedAllChecked, setIsSelectedAllChecked] = useState(false);
  const [isAccessDenied, setAccessDenied] = useState(false);
  const [displayList, setDisplayList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [shouldReverse, setShouldReverse] = useState(false);
  const [sortedByValue, setSortedByValue] = useState(null);
  const initialValues = { users: [] };
  useEffect(() => {
    checkIfApproved(jurisdictions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function checkIfApproved(jurisdictions) {
    if (!isAdminApproved(jurisdictions) && application !== 'central_station') {
      setAccessDenied(true);
    } else {
      setAccessDenied(false);
    }
  }

  const setSortedDisplayList = useCallback(() => {
    const newDisplayList = currentPSAPUsers.filter(user => {
      return (
        user.email.includes(searchTerm.toLowerCase()) ||
        user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
    const sortedDisplayList = handleSortingPSAPUsers(
      newDisplayList,
      sortedByValue,
      shouldReverse,
    );
    setDisplayList(sortedDisplayList);
  }, [currentPSAPUsers, searchTerm, sortedByValue, shouldReverse]);

  useEffect(() => {
    setSortedDisplayList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const toggleAddUserModal = () => {
    if (isAdminApproved(jurisdictions) || application === 'central_station') {
      setAddUserModalIsActive(prev => !prev);
    } else {
      notifyError(
        "Your agency's jsurisdiction has not been approved yet. Approval can take up to 15 days. ",
      );
    }
  };
  const toggleEditUserModal = user => {
    if (user && !editUserModalIsActive) {
      setEditUserModalIsActive(user);
    } else {
      setEditUserModalIsActive(false);
    }
  };

  const toggleDeleteUserModal = values => {
    if (values?.users?.length && !deleteUserModalIsActive) {
      setDeleteUserModalIsActive(true);
    } else {
      setDeleteUserModalIsActive(false);
    }
  };

  const handleCheckboxChange = (e, arrayHelpers, user, values) => {
    if (e.target.checked) {
      if (values.users.length + 1 === displayList.length) {
        setIsSelectedAllChecked(true);
      }
      arrayHelpers.push(user);
    } else {
      if (values.users.length - 1 === 0) {
        setIsSelectedAllChecked(false);
      }
      const targetUser = values.users.find(u => u.email === user.email);
      const idx = values.users.indexOf(targetUser);
      arrayHelpers.remove(idx);
    }
  };

  const handleSelectedAllChecked = (values, setValues) => {
    if (isSelectedAllChecked) {
      setValues({ ...values, ...{ users: [] } });
      setIsSelectedAllChecked(false);
    } else {
      const users = [...displayList];
      setValues({ ...values, ...{ users } });
      setIsSelectedAllChecked(true);
    }
  };
  useEffect(() => {
    setSortedDisplayList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPSAPUsers]);

  const sortPSAPUsers = (currentPSAPUsers, sortedByValue) => {
    setDisplayList(
      handleSortingPSAPUsers(currentPSAPUsers, sortedByValue, !shouldReverse),
    );
    setSortedByValue(sortedByValue);
    setShouldReverse(prev => !prev);
  };

  return (
    <UsersPageContainer>
      {isAccessDenied && (
        <AccessDenied
          handleAccessDeniedLink={handleAccessDeniedLink}
          height="100%"
        />
      )}
      <Formik initialValues={initialValues}>
        {({ values, setValues }) => (
          <FieldArray name="users">
            {arrayHelpers => (
              <Form id="usersForm">
                <Box>
                  <Text size="header" fontWeight="bold" color="primaryText">
                    Users
                  </Text>
                  <AddButtonContainer horizontal>
                    <Text fontWeight="semibold" color="primaryText">
                      Add new users and assign roles
                    </Text>

                    <Button btnType="primary" onClick={toggleAddUserModal}>
                      Add New User
                    </Button>
                  </AddButtonContainer>
                </Box>
                <Box mr={'5px'}>
                  <Box>
                    <UserSearchBar
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                    />
                    <UsersContainer
                      orgID={orgID}
                      searchTerm={searchTerm}
                      application={application}
                      currentPSAPUsers={displayList}
                      toggleEditUserModal={toggleEditUserModal}
                      isSelectedAllChecked={isSelectedAllChecked}
                      toggleDeleteUserModal={toggleDeleteUserModal}
                      handleSelectedAllChecked={handleSelectedAllChecked}
                      handleCheckboxChange={handleCheckboxChange}
                      values={values}
                      setValues={setValues}
                      arrayHelpers={arrayHelpers}
                      setShouldReverse={setShouldReverse}
                      setSortedByValue={setSortedByValue}
                      shouldReverse={shouldReverse}
                      sortedByValue={sortedByValue}
                      sortPSAPUsers={sortPSAPUsers}
                    />
                  </Box>
                </Box>
                {deleteUserModalIsActive && (
                  <DeleteUserModal
                    orgID={orgID}
                    application={application}
                    isOpened={deleteUserModalIsActive}
                    toggleDeleteUserModal={toggleDeleteUserModal}
                    arrayHelpers={arrayHelpers}
                    values={values}
                  />
                )}
                {editUserModalIsActive && (
                  <EditUserModal
                    orgID={orgID}
                    allRoles={allRoles}
                    application={application}
                    user={editUserModalIsActive}
                    isOpened={!!editUserModalIsActive}
                    toggleEditUserModal={toggleEditUserModal}
                  />
                )}
                {addUserModalIsActive && (
                  <AddUserModal
                    orgID={orgID}
                    allRoles={allRoles}
                    application={application}
                    isOpened={addUserModalIsActive}
                    currentPSAPUsers={currentPSAPUsers}
                    toggleAddUserModal={toggleAddUserModal}
                  />
                )}
              </Form>
            )}
          </FieldArray>
        )}
      </Formik>
    </UsersPageContainer>
  );
};

export default Users;
